import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * הלוח השבועי: שבוע אחד על פני כמה סניפים (מיגרציה 046).
 *
 * בניגוד ל schedule_board שמביא תקופה אחת, כאן היחידה היא השבוע
 * והסניפים הם מסנן. זה מה שמאפשר לראות את כל הרשת במסך אחד.
 */
export type WeekBranch = {
  id: string;
  name: string;
  color: string | null;
  period_id: string | null;
  status: 'collecting' | 'drafting' | 'published' | 'archived' | null;
  constraints_deadline: string | null;
};

export type WeekShiftType = {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  color: string | null;
  sort_order: number;
};

export type WeekStaff = {
  id: string;
  name: string;
  job_title_id: string | null;
  job_title: string | null;
  branch_ids: string[];
};

/**
 * דרישת כוח אדם אחת. `job_title_id` ריק = "כל תפקיד".
 *
 * השרת מחזיר שורה לכל תפקיד ולא סכום, כי הסכום איבד את השאלה
 * "כמה קופאים" ואפשר היה לשבץ חמישה מוכרים ולראות משמרת מלאה.
 */
export type WeekRequirement = {
  branch_id: string;
  shift_type_id: string;
  weekday: number;
  job_title_id: string | null;
  job_title: string | null;
  required_count: number;
};

export type WeekAssignment = {
  id: string;
  date: string;
  branch_id: string;
  shift_type_id: string;
  employee_id: string;
  /** manual | ai | swap | copied */
  source: string;
  /** null = כמו המשמרת. לא "לא ידוע". */
  start_time: string | null;
  end_time: string | null;
  effective_start: string;
  effective_end: string;
  note: string | null;
};

export type WeekConstraint = {
  employee_id: string;
  date: string;
  shift_type_id: string | null;
  is_blocking: boolean;
  /** שלילי = חוסם או מעדיף שלא, חיובי = מעדיף לעבוד. */
  weight: number;
  color: string | null;
  name: string;
  note: string | null;
};

export type WeekBoard = {
  week_start: string;
  week_end: string;
  days: string[];
  branches: WeekBranch[];
  shift_types: WeekShiftType[];
  staff: WeekStaff[];
  assignments: WeekAssignment[];
  constraints: WeekConstraint[];
  requirements: WeekRequirement[];
  closures: {
    branch_id: string;
    date: string | null;
    shift_type_id: string | null;
    is_closed: boolean;
    label: string;
  }[];
};

// שעות: ראו features/schedule/hoursApi. מיוצא כאן מחדש כדי
// שהיבוא הקיים בלוח לא ישתנה.
export { useScheduleHours, type HoursRow } from '../hoursApi';

/** מה קרה בהעתקת השבוע: כמה עברו, וכמה דולגו ולמה. */
export type CopyResult = {
  copied: number;
  busy: number;
  closed: number;
  gone: number;
  /** פירוט לפי סניף, רק למי שייווצר בו משהו (מיגרציה 096). */
  branches: { branch_id: string; name: string; copied: number }[];
};

/**
 * השבוע נטען תמיד עם כל הסניפים, ובחירת הסניפים מסננת בממשק.
 *
 * כששלחנו את הבחירה לשרת, רשימת הסניפים שחזרה הייתה הרשימה
 * המסוננת, ולכן אחרי לחיצה על סניף אחד לא הייתה דרך לחזור לשאר.
 * שבוע של כל הרשת הוא בקשה קטנה, והחלפת סניף עכשיו מיידית.
 */
export function useScheduleWeek(weekStart: string) {
  return useQuery({
    queryKey: ['schedule-week', weekStart] as const,
    queryFn: async (): Promise<WeekBoard> => {
      const { data, error } = await supabase.rpc('schedule_week', {
        p_week_start: weekStart,
      });
      if (error) throw error;
      return data as unknown as WeekBoard;
    },
  });
}

/** הלוח כפי שהוא מוצג: רק הסניפים שנבחרו. ריק = כולם. */
export function visibleBoard(board: WeekBoard, branchIds: string[]): WeekBoard {
  if (branchIds.length === 0) return board;

  const shown = new Set(branchIds);
  const branches = board.branches.filter((branch) => shown.has(branch.id));

  return {
    ...board,
    branches,
    assignments: board.assignments.filter((row) => shown.has(row.branch_id)),
    requirements: board.requirements.filter((row) => shown.has(row.branch_id)),
    closures: board.closures.filter((row) => shown.has(row.branch_id)),
    // עובד שמשויך לכמה סניפים נשאר ברשימה כל עוד אחד מהם מוצג
    staff: board.staff.filter((person) => person.branch_ids.some((id) => shown.has(id))),
  };
}

export function useWeekMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['schedule-week'] });
    // כל שינוי בשיבוץ משנה גם את סיכום השעות. בלי זה המד של
    // החודש נשאר על מספר ישן עד שמישהו מרענן.
    void queryClient.invalidateQueries({ queryKey: ['schedule-hours'] });
  };

  const assign = useMutation({
    mutationFn: async (input: {
      branch_id: string;
      date: string;
      shift_type_id: string;
      employee_id: string;
    }) => {
      const { error } = await supabase.rpc('assign_shift', {
        p_branch: input.branch_id,
        p_date: input.date,
        p_shift_type: input.shift_type_id,
        p_employee: input.employee_id,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const move = useMutation({
    mutationFn: async (input: {
      assignment_id: string;
      branch_id: string;
      date: string;
      shift_type_id: string;
    }) => {
      const { error } = await supabase.rpc('move_shift', {
        p_assignment: input.assignment_id,
        p_branch: input.branch_id,
        p_date: input.date,
        p_shift_type: input.shift_type_id,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const unassign = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('shift_assignments').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /**
   * עריכת שיבוץ קיים. עוברת דרך RPC ולא דרך update על הטבלה,
   * כי ה RLS מתיר למנהל לערוך גם שבוע שפורסם (מיגרציה 048).
   */
  const updateAssignment = useMutation({
    mutationFn: async (input: {
      assignment_id: string;
      start_time: string | null;
      end_time: string | null;
      note: string | null;
      shift_type_id: string;
    }) => {
      // ברירת המחדל של כל פרמטר בשרת היא null, ולכן השמטה וריק הם
      // אותו דבר: "כמו המשמרת" לשעות, ו"בלי הערה" להערה
      const { error } = await supabase.rpc('update_assignment', {
        p_assignment: input.assignment_id,
        p_start: input.start_time ?? undefined,
        p_end: input.end_time ?? undefined,
        p_note: input.note ?? undefined,
        p_shift_type: input.shift_type_id,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const copyWeek = useMutation({
    mutationFn: async (input: {
      week_start: string;
      branch_ids: string[];
      /** ספירה בלבד, בלי ליצור כלום (מיגרציה 096). */
      dryRun?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('copy_schedule_week', {
        p_target_week: input.week_start,
        p_branches: input.branch_ids.length > 0 ? input.branch_ids : undefined,
        p_dry_run: input.dryRun ?? false,
      });
      if (error) throw error;
      return data as unknown as CopyResult;
    },
    // ריצה יבשה אינה משנה כלום, ולכן אין מה לרענן אחריה
    onSuccess: (_data, input) => {
      if (!input.dryRun) refresh();
    },
  });

  const closeShift = useMutation({
    mutationFn: async (input: {
      branch_id: string;
      date: string;
      shift_type_id: string | null;
      label: string;
    }) => {
      const { data, error } = await supabase.rpc('close_shift', {
        p_branch: input.branch_id,
        p_date: input.date,
        p_shift_type: input.shift_type_id ?? undefined,
        p_label: input.label,
      });
      if (error) throw error;
      // כמה שיבוצים ירדו יחד עם הסגירה
      return (data as unknown as number) ?? 0;
    },
    onSuccess: refresh,
  });

  const reopenShift = useMutation({
    mutationFn: async (input: {
      branch_id: string;
      date: string;
      shift_type_id: string | null;
    }) => {
      const { error } = await supabase.rpc('reopen_shift', {
        p_branch: input.branch_id,
        p_date: input.date,
        p_shift_type: input.shift_type_id ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { assign, move, unassign, copyWeek, updateAssignment, closeShift, reopenShift };
}


/**
 * כמה מהדרישה של כל השבוע כבר מאויש, בסניף אחד.
 *
 * זו השאלה הראשונה של מי שנכנס למסך, ועד עכשיו התשובה היחידה
 * הייתה לספור ארבעה עשר תאים בעיניים.
 *
 * יום סגור אינו נספר: אין בו דרישה, והוא לא אמור להוריד את
 * האחוז. שיבוץ מעבר לדרוש נספר עד הדרוש בלבד — שלושה אנשים
 * במשמרת שצריכה אחד אינם שלוש מאות אחוז.
 */
export function branchFill(board: WeekBoard, branchId: string): { filled: number; required: number } {
  let filled = 0;
  let required = 0;

  for (const day of board.days) {
    const weekday = new Date(`${day}T12:00:00`).getDay();

    for (const shift of board.shift_types) {
      const closed = board.closures.some(
        (closure) =>
          closure.branch_id === branchId &&
          closure.is_closed &&
          (closure.date === day || closure.date === null) &&
          (closure.shift_type_id === null || closure.shift_type_id === shift.id),
      );
      if (closed) continue;

      const need = board.requirements
        .filter(
          (rule) =>
            rule.branch_id === branchId &&
            rule.shift_type_id === shift.id &&
            rule.weekday === weekday,
        )
        .reduce((total, rule) => total + rule.required_count, 0);

      const here = board.assignments.filter(
        (assignment) =>
          assignment.branch_id === branchId &&
          assignment.date === day &&
          assignment.shift_type_id === shift.id,
      ).length;

      required += need;
      filled += Math.min(here, need);
    }
  }

  return { filled, required };
}
